// Implements: F-UIX-014, F-MCK-006, F-MCK-015, F-UIX-007, F-UIX-009, F-UIX-010, F-UIX-015
/**
 * The mockup's own detail on its page: its name, its dates and its tags, and, for whoever may
 * edit it, renaming in place and adding or removing tags (the header of F-MCK-006 and F-MCK-015).
 *
 * Everything shown comes from `GET /mockups/{id}` (the SHARED detail query): an edit is not
 * optimistic, it writes and then reads the mockup again, so a refusal leaves the page exactly as
 * the server has it. What is offered follows `your_role` (`owner` and `editor` may rename and tag,
 * the matrix of `domain/permissions.py`); the server still decides, and its refusal is read out
 * next to the field (`role="alert"`). The writes are never retried (`retry: false`, checklist 13).
 *
 * The keyboard focus never falls to the page after a write (WCAG 2.4.3): a field being saved is
 * read-only, not disabled, so it keeps the focus; a refused rename gives it back to the name; a
 * written or refused tag gives it back to the tag field, and so does a removed tag, whose button
 * is gone. One write at a time: a second press while one is in flight is ignored.
 *
 * A refusal after which the refetch says the person may no longer edit (`your_role` became
 * `viewer`) takes the fields that would read it out away: it is then announced as a notice
 * (`role="alert"`) and the focus goes to the mockup's name, never to the page.
 *
 * The container is keyed by mockup on the page (checklist 14); an answer that arrives after it was
 * left behind is dropped here, so it can never announce a success on another mockup. A mockup that
 * does not exist renders nothing: the page keeps its clean starting state (F-UIX-014).
 */

import { useEffect, useId, useRef, useState } from "react";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Input } from "../../../shared/ui/atoms/Input";
import { dayText } from "../../../shared/ui/dayText";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { Field, describedBy } from "../../../shared/ui/molecules/Field";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { MockupTagList } from "../components/MockupTagList";
import { MAX_TAG_LENGTH, codePoints, normalizeTag, tagProblem } from "../components/tagDraft";
import { useMockupActions } from "../hooks/useMockupActions";
import { useMockupDetail } from "../hooks/useMockupDetail";
import styles from "./MockupDetailContainer.module.css";

/** The bound of `MockupName` (`domain/value_objects/mockup_name.py`). */
export const MAX_NAME_LENGTH = 120;

const EDITOR_ROLES: ReadonlySet<string> = new Set(["owner", "editor"]);

export interface MockupDetailContainerProps {
  readonly mockupId: string;
  /**
   * Which part (UX-05): `all` (default), `tags` (the topbar's tag panel: the tags and the tag
   * field) or `facts` (the drawer's details: the dates and the tags, which an editor still edits
   * there, the only tag control below 1280 px; the name is the topbar's).
   *
   * The editor mounts `tags` and `facts` only; renaming lives in `MockupNameContainer`. `all`
   * (with the in-place rename) is KEPT on purpose (UX-13 review): the F-UIX-007 partial-feature
   * note of `frontend/architecture.json` and `tests/architecture/deferred.test.ts` cite its rename
   * focus contract ("vuelve al botón Renombrar…") and this file's tests as their proof, and both
   * files are serial. Removing `all` is a follow-up for their holder, together with that note.
   */
  readonly section?: "all" | "tags" | "facts" | undefined;
}

export function MockupDetailContainer({ mockupId, section = "all" }: MockupDetailContainerProps): ReactElement | null {
  const { t } = useTranslation();
  const mockup = useMockupDetail(mockupId);
  const { rename, addTag, removeTag, currentRole } = useMockupActions();
  const { push } = useToast();
  const baseId = useId();
  const renameButtonId = `${baseId}-rename`;
  const nameId = `${baseId}-name`;
  const tagId = `${baseId}-tag`;
  const nameInput = useRef<HTMLInputElement | null>(null);
  const nameForm = useRef<HTMLFormElement | null>(null);
  const tagInput = useRef<HTMLInputElement | null>(null);
  const heading = useRef<HTMLHeadingElement | null>(null);
  // The section itself: the focus target when the name is not part of it (`tags`, `facts`).
  const region = useRef<HTMLElement | null>(null);
  // False once this container is left behind: a late answer then changes nothing on screen.
  const alive = useRef(true);
  const [draft, setDraft] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [tagDraft, setTagDraft] = useState("");
  const [tagError, setTagError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  // Set when a refusal took the editing away: the name takes the focus once the editor is gone.
  const [focusName, setFocusName] = useState(false);
  // The lock itself: read synchronously, so two presses in the same tick still send one write.
  const inFlight = useRef(false);
  const wasEditing = useRef(false);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Only someone who may still rename keeps the editor: a refetch after a refusal can say otherwise.
  const mayEditNow = mockup.data !== undefined && EDITOR_ROLES.has(mockup.data.your_role);
  const editing = draft !== null && mayEditNow;
  useEffect(() => {
    if (!editing) return undefined;
    nameInput.current?.focus();
    // Escape inside the editor closes it as it opened, from the keyboard. Listened on the editor
    // only, so an Escape meant for a dialog elsewhere on the page never discards a typed name.
    const form = nameForm.current;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setDraft(null);
      setNameError(undefined);
    };
    form?.addEventListener("keydown", onKey);
    return () => {
      form?.removeEventListener("keydown", onKey);
    };
  }, [editing]);
  // Closing the editor (saved or cancelled) gives the focus back to the button that opened it.
  useEffect(() => {
    if (wasEditing.current && !editing) document.getElementById(renameButtonId)?.focus();
    wasEditing.current = editing;
  }, [editing, renameButtonId]);
  // Declared after the effect above, so it wins over a rename button that is no longer there.
  useEffect(() => {
    if (!focusName || editing) return;
    // The editor's sections carry no name (it is the topbar's): the focus goes to the section,
    // whose label says what it is, instead of falling to the page (UX-13).
    (heading.current ?? region.current)?.focus();
  }, [focusName, editing]);

  if (mockup.isPending) return <LoadingState label={t("common.state.loading")} />;
  if (mockup.isError) {
    // An address that names no mockup is the page's clean state, not a failure of this block.
    if (mockup.error.status === 404) return null;
    return (
      <ErrorState
        error={mockup.error}
        onRetry={() => {
          void mockup.refetch();
        }}
      />
    );
  }

  const view = mockup.data;
  const mayEdit = mayEditNow;

  const closeEditor = (): void => {
    setDraft(null);
    setNameError(undefined);
  };

  /**
   * Runs one write; answers `false` (and changes nothing) when another write is still in flight or
   * the container was left meanwhile.
   */
  const write = async (call: () => Promise<ApiError | null>): Promise<ApiError | null | false> => {
    if (inFlight.current) return false;
    inFlight.current = true;
    setSaving(true);
    setFocusName(false);
    const refusal = await call();
    inFlight.current = false;
    if (!alive.current) return false;
    setSaving(false);
    return refusal;
  };

  /**
   * Whether a refusal took the editing away (the refetch it triggered says so). If it did, the
   * refusal is announced as a notice, since the field that would show it is gone, and the focus
   * goes to the mockup's name.
   */
  const lostEditing = (refusal: ApiError, fallback: string): boolean => {
    if (EDITOR_ROLES.has(currentRole(mockupId) ?? "")) return false;
    push({ tone: "danger", message: userMessage(refusal, fallback) });
    setFocusName(true);
    return true;
  };

  const submitName = async (): Promise<void> => {
    if (draft === null) return;
    const name = draft.trim();
    if (name.length === 0) {
      setNameError(t("mockup.detail.nameEmpty"));
      return;
    }
    if (codePoints(name) > MAX_NAME_LENGTH) {
      setNameError(t("mockup.detail.nameTooLong", { max: MAX_NAME_LENGTH }));
      return;
    }
    const refusal = await write(() => rename(mockupId, name));
    if (refusal === false) return;
    if (refusal !== null) {
      if (lostEditing(refusal, t("mockup.detail.renameFailed"))) {
        closeEditor();
        return;
      }
      setNameError(userMessage(refusal, t("mockup.detail.renameFailed")));
      nameInput.current?.focus();
      return;
    }
    closeEditor();
    push({ tone: "success", message: t("mockup.detail.renamed") });
  };

  const submitTag = async (): Promise<void> => {
    const problem = tagProblem(tagDraft);
    if (problem !== null) {
      setTagError(
        problem === "empty"
          ? t("mockup.detail.tagEmpty")
          : problem === "tooLong"
            ? t("mockup.detail.tagTooLong", { max: MAX_TAG_LENGTH })
            : problem === "dots"
              ? t("mockup.detail.tagDots")
              : t("mockup.detail.tagSlash"),
      );
      return;
    }
    const refusal = await write(() => addTag(mockupId, normalizeTag(tagDraft)));
    if (refusal === false) return;
    if (refusal !== null && lostEditing(refusal, t("mockup.detail.tagFailed"))) return;
    tagInput.current?.focus();
    if (refusal !== null) {
      setTagError(userMessage(refusal, t("mockup.detail.tagFailed")));
      return;
    }
    setTagDraft("");
    setTagError(undefined);
    push({ tone: "success", message: t("mockup.detail.tagAdded") });
  };

  const dropTag = async (tag: string): Promise<void> => {
    const refusal = await write(() => removeTag(mockupId, tag));
    if (refusal === false) return;
    if (refusal !== null) {
      if (lostEditing(refusal, t("mockup.detail.tagFailed"))) return;
      setTagError(userMessage(refusal, t("mockup.detail.tagFailed")));
      return;
    }
    setTagError(undefined);
    // Its button is gone with it: the tag field takes the focus instead of the page.
    tagInput.current?.focus();
    push({ tone: "success", message: t("mockup.detail.tagRemoved") });
  };

  return (
    <section
      ref={region}
      className={styles.detail}
      aria-label={t("mockup.detail.label")}
      aria-busy={saving}
      tabIndex={-1}
    >
      {section !== "all" ? null : !editing ? (
        <div className={styles.header}>
          <h2 className={styles.name} ref={heading} tabIndex={-1}>
            {view.name}
          </h2>
          {mayEdit ? (
            <Button
              variant="secondary"
              size="sm"
              id={renameButtonId}
              onClick={() => {
                setFocusName(false);
                setDraft(view.name);
              }}
            >
              {t("mockup.detail.rename")}
            </Button>
          ) : null}
        </div>
      ) : (
        <form
          ref={nameForm}
          className={styles.form}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void submitName();
          }}
        >
          <Field id={nameId} label={t("mockup.detail.nameLabel")} error={nameError}>
            <Input
              id={nameId}
              inputRef={nameInput}
              value={draft}
              onChange={setDraft}
              invalid={nameError !== undefined}
              describedBy={describedBy(nameId, { error: nameError })}
              readOnly={saving}
            />
          </Field>
          <Button variant="primary" size="sm" type="submit" loading={saving}>
            {t("mockup.detail.save")}
          </Button>
          <Button variant="ghost" size="sm" disabled={saving} onClick={closeEditor}>
            {t("mockup.detail.cancel")}
          </Button>
        </form>
      )}
      {section === "tags" ? null : (
      <dl className={styles.dates}>
        <div className={styles.date}>
          <dt>{t("mockup.detail.created")}</dt>
          <dd>
            <time dateTime={view.created_at}>{dayText(view.created_at)}</time>
          </dd>
        </div>
        <div className={styles.date}>
          <dt>{t("mockup.detail.updated")}</dt>
          <dd>
            <time dateTime={view.updated_at}>{dayText(view.updated_at)}</time>
          </dd>
        </div>
      </dl>
      )}
      <MockupTagList
        tags={view.tags}
        onRemove={
          mayEdit
            ? (tag) => {
                void dropTag(tag);
              }
            : undefined
        }
      />
      {mayEdit ? (
        <form
          className={styles.form}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void submitTag();
          }}
        >
          <Field id={tagId} label={t("mockup.detail.tagLabel")} error={tagError}>
            <Input
              id={tagId}
              inputRef={tagInput}
              value={tagDraft}
              onChange={setTagDraft}
              invalid={tagError !== undefined}
              describedBy={describedBy(tagId, { error: tagError })}
              readOnly={saving}
            />
          </Field>
          <Button variant="secondary" size="sm" type="submit" loading={saving}>
            {t("mockup.detail.addTag")}
          </Button>
        </form>
      ) : null}
    </section>
  );
}
